<script setup lang="ts">
import CookieBanner from '~/domains/cookies/components/CookieBanner.vue'
import BOCFloating from '~/domains/operations/components/BOCFloating.vue'
import RestaurantOperationsProvider from '~/domains/operations/components/RestaurantOperationsProvider.vue'

const PWA_PROMPT_KEY = 'pwa-prompt-dismissed'

const route = useRoute()

const { t } = useI18n()

const head = useLocaleHead()

const { siteName } = useAppConfig()

const { $pwa } = useNuxtApp()

function triggerPwaPrompt() {
  if (!$pwa || $pwa.isPWAInstalled || !$pwa.showInstallPrompt) {
    return
  }

  const wasPwaPromptDismissed = localStorage.getItem(PWA_PROMPT_KEY) === 'true'
  if (wasPwaPromptDismissed) {
    return
  }

  $pwa.install().then((choice) => {
    if (choice?.outcome) {
      localStorage.setItem(PWA_PROMPT_KEY, 'true')
    }
  })
}

const title = computed(() =>
  route.meta.title
    ? t('shared.seo.title', { title: t(route.meta.title as string) })
    : siteName,
)
</script>

<template>
  <Html :lang="head.htmlAttrs!.lang" :dir="head.htmlAttrs!.dir">
    <Head>
      <Title>{{ title }}</Title>
      <template v-for="link in head.link" :key="link.id">
        <Link
          :id="link.id"
          :rel="link.rel"
          :href="link.href"
          :hreflang="'hreflang' in link ? link.hreflang : undefined"
        />
      </template>
      <template v-for="meta in head.meta" :key="meta.id">
        <Meta :id="meta.id" :property="meta.property" :content="String(meta.content)" />
      </template>
      <Link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <Link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <Link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <NuxtPwaManifest />
      <Link rel="mask-icon" href="/safari-pinned-tab.svg" color="#f43f5e" />
      <Meta name="msapplication-TileColor" content="#f43f5e" />
      <Meta name="theme-color" content="#f43f5e" />
    </Head>
    <Body
      :onclick="triggerPwaPrompt"
    >
      <UApp>
        <NuxtLayout>
          <NuxtLoadingIndicator color="#f43f5e" />
          <RestaurantOperationsProvider>
            <NuxtPage />
            <BOCFloating />
          </RestaurantOperationsProvider>
          <CookieBanner />
        </NuxtLayout>
      </UApp>
      <!--
        Floating-action-button slot. FabAction and BOCFloating teleport their
        buttons here; flex-row-reverse keeps the first-mounted child in the
        bottom-right corner and stacks subsequent ones leftward.
      -->
      <div
        id="fab-slot"
        class="pointer-events-none fixed bottom-6 right-6 z-40 flex flex-row-reverse items-end gap-3"
      />
    </Body>
  </Html>
</template>
